'use client'

import { useSetAtom } from 'jotai'
import { useRouter } from 'next/navigation'
import { useCallback } from 'react'

import { getTradePageIdFromPath, getTradePagePath } from '@/features/dashboard/lib/trade-page-path'
import { activePageIdAtom, pendingDashboardPageNavigationAtom } from '@/state/dashboard-layouts'

// Dashboard page switches don't need the server: every /trade/[pageId] renders
// the same client shell, so while that route is mounted a switch is a pure
// pushState — usePathname syncs, TradePageClient remounts DashboardLayout under
// the new key, no RSC round trip. From any other route (/billing, /trade) fall
// back to a real navigation, with the pending atom driving the instant tab
// highlight and loading feedback until it commits.
export function useNavigateToTradePage() {
  const router = useRouter()
  const setActivePageId = useSetAtom(activePageIdAtom)
  const setPendingNavigationPageId = useSetAtom(pendingDashboardPageNavigationAtom)

  return useCallback(
    (pageId: string, options?: { replace?: boolean }) => {
      const path = getTradePagePath(pageId)
      if (window.location.pathname === path) return

      if (getTradePageIdFromPath(window.location.pathname)) {
        // Last click wins — supersede any in-flight cross-route navigation's highlight.
        setPendingNavigationPageId(null)
        setActivePageId(pageId)
        if (options?.replace) window.history.replaceState(null, '', path)
        else window.history.pushState(null, '', path)
        return
      }

      setPendingNavigationPageId(pageId)
      if (options?.replace) router.replace(path)
      else router.push(path)
    },
    [router, setActivePageId, setPendingNavigationPageId]
  )
}
